<script setup lang="ts">
import { ref } from "vue";

import { diagnosticsText, type PresentedError } from "@/lib/errors";

const props = defineProps<{ error: PresentedError; action: string }>();
const copyStatus = ref<"idle" | "copied" | "failed">("idle");

async function copyDiagnostics() {
  try {
    await navigator.clipboard.writeText(diagnosticsText(props.action, props.error));
    copyStatus.value = "copied";
  } catch {
    copyStatus.value = "failed";
  }
  window.setTimeout(() => (copyStatus.value = "idle"), 2400);
}
</script>

<template>
  <section class="error-panel" role="alert">
    <span class="status-dot danger" aria-hidden="true"></span>
    <div>
      <p class="eyebrow">{{ error.kind }}</p>
      <h2>{{ error.title }}</h2>
      <p>{{ error.message }}</p>
      <ul v-if="error.validationIssues.length" class="validation-issues">
        <li v-for="issue in error.validationIssues" :key="issue">{{ issue }}</li>
      </ul>
      <details v-if="error.status || error.code || error.requestId" class="technical-details">
        <summary>Technical details</summary>
        <dl>
          <template v-if="error.status"
            ><dt>Status</dt>
            <dd>{{ error.status }}</dd></template
          >
          <template v-if="error.code"
            ><dt>Code</dt>
            <dd>{{ error.code }}</dd></template
          >
          <template v-if="error.requestId"
            ><dt>Request ID</dt>
            <dd>{{ error.requestId }}</dd></template
          >
        </dl>
        <p class="support-hint">
          Include these diagnostics in the team incident or support channel. The request ID lets
          responders correlate this failure with API and proxy logs.
        </p>
        <button class="text-button" type="button" @click="copyDiagnostics">
          {{
            copyStatus === "copied"
              ? "Copied"
              : copyStatus === "failed"
                ? "Copy failed"
                : "Copy diagnostics"
          }}
        </button>
        <span class="sr-only" aria-live="polite">
          {{
            copyStatus === "copied"
              ? "Diagnostics copied to clipboard"
              : copyStatus === "failed"
                ? "Could not copy diagnostics"
                : ""
          }}
        </span>
      </details>
    </div>
  </section>
</template>
